/// <amd-module name="Environments/Providers/VirtualMachineGroups/VirtualMachineGroupsContainers" />
import * as React from "react";

import { Components, IVssComponentContext } from "VSS/Platform/Layout";
import { ObservableValue } from "VSS/Core/Observable";
import { Spinner, SpinnerSize } from 'VSSUI/Spinner';
import { Page } from "VSSUI/Page";
import { Surface, SurfaceBackground } from "VSSUI/Surface";
import { Header, TitleSize } from "VSSUI/Header";
import { Tab, TabBar, TabContent } from "VSSUI/Tabs";

import { addBreadcrumbs } from "Environments/Breadcrumb/EnvironmentsHubBreadcrumbService";
import {
    EnvironmentBaseComponent,
    IEnvironmentBaseComponentProperties,
    IEnvironmentBaseComponentState
} from "Environments/EnvironmentsCommon/EnvironmentBaseComponent";
import * as Resources from "Environments/Providers/VirtualMachineGroups/Resources";
import { StoreManager } from "Environments/Flux/StoreManager";
import { ActionCreatorManager } from "Environments/Flux/ActionCreatorManager";
import { VirtualMachineGroupTargets } from "Environments/Providers/VirtualMachineGroups/Components/VirtualMachineGroupTargets";
import { getCurrentEnvironmentIdFromRoute, getEnvironmentPageUrl, getResourceIdFromRoute, getResourcePageUrl } from "Environments/EnvironmentsCommon/Link";
import { VirtualMachineGroupStore } from "Environments/Providers/VirtualMachineGroups/Stores/VirtualMachineGroupStore";
import { VirtualMachineGroupActionCreator } from "Environments/Providers/VirtualMachineGroups/ActionsCreators/VirtualMachineGroupActionCreator";
import { VirtualMachineGroup } from "DistributedTask/Client/Contracts/TaskAgent";
import { PageKey } from "Environments/Providers/VirtualMachineGroups/Utilities";

interface IVirtualMachineGroupsContainerState extends IEnvironmentBaseComponentState {
    virtualMachineGroup: VirtualMachineGroup | null;
    notLoaded?: boolean;
}

class VirtualMachineGroupsContainer extends EnvironmentBaseComponent<IEnvironmentBaseComponentProperties, IVirtualMachineGroupsContainerState> {
    constructor(props: IEnvironmentBaseComponentProperties, context: IVssComponentContext) {
        super(props, context);

        this.state = {
            virtualMachineGroup: null,
            notLoaded: true,
            errorMessages: {}
        };

        this._environmentId = getCurrentEnvironmentIdFromRoute(this.context.pageContext);
        this._virtualMachineGroupId = getResourceIdFromRoute(this.context.pageContext);
    }

    public getInstanceId(): string {
        return "virtualmachinegroup-instance-id";
    }

    public componentWillMount() {
        this._store = StoreManager.CreateStore<VirtualMachineGroupStore>(VirtualMachineGroupStore, this.getInstanceId());
        this._store.addChangedListener(this._onVirtualMachineGroupStoreGetChange);

        this._actionCreator = ActionCreatorManager.CreateActionCreator<VirtualMachineGroupActionCreator>(VirtualMachineGroupActionCreator, this.getInstanceId());
        this._actionCreator.getVirtualMachineGroup(this.context.pageContext, this._environmentId, this._virtualMachineGroupId);
    }

    public render(): React.ReactNode {

        const { virtualMachineGroup } = this.state;

        if (this.state.notLoaded) {
            return (
                <div className="full-size flex-column justify-center">
                    <Spinner size={SpinnerSize.large} label={Resources.Loading} />
                </div>
            );
        }

        let virtualMachineGroupSummary: JSX.Element = <div></div>;
        if (virtualMachineGroup) {
            virtualMachineGroupSummary = (
                <Header title={virtualMachineGroup.name} titleSize={TitleSize.Large} />
            );
        }

        return (
            <Surface background={SurfaceBackground.neutral}>
                <Page className="flex-grow" selectedTabId={this._selectedTabId}>
                    {this.getErrorMessageView(PageKey.VirtualMachineGroupTargetsView)}
                    {virtualMachineGroupSummary}
                    <TabBar onSelectedTabChanged={this._onSelectedTabChanged}>
                        <Tab name={Resources.WorkloadsText} id={this.resourcesTabKey} />
                    </TabBar>
                    <TabContent>
                        {
                            this._selectedTabId.value == this.resourcesTabKey && this._getResourcesTabContent()
                        }
                    </TabContent>
                </Page>
            </Surface>
        );
    }

    private _getResourcesTabContent = (): React.ReactNode => {
        if (this.state.virtualMachineGroup) {
            return (<VirtualMachineGroupTargets environmentId={this._environmentId} virtualMachineGroup={this.state.virtualMachineGroup} />);
        }
        return undefined;
    }

    private _onVirtualMachineGroupStoreGetChange = (): void => {
        const virtualMachineGroup = this._store.getVirtualMachineGroup();

        // Add breadcrumb for this virtual machine group
        this._addBreadcrumbs(virtualMachineGroup);

        // Re-render the page using the acquired virtual machine group
        this.setState({ virtualMachineGroup, notLoaded: false });
    }

    private _addBreadcrumbs(virtualMachineGroup: VirtualMachineGroup): void {
        if (virtualMachineGroup) {
            const { pageContext } = this.context;
            
            const environment = virtualMachineGroup.environmentReference;
            const environmentPageUrl = getEnvironmentPageUrl(pageContext, environment.id);
            
            const vmgPageUrl = getResourcePageUrl(
                pageContext,
                environment.id,
                virtualMachineGroup.id,
                "ms.vss-environments-web.virtualmachinegroups-summary-default-route"
            );

            addBreadcrumbs(
                pageContext, 
                { name: environment.name, url: environmentPageUrl },
                { name: virtualMachineGroup.name, url: vmgPageUrl }
            );
        }
    }

    private _onSelectedTabChanged = (tabId: string): void => {
        this._selectedTabId.value = tabId;
    }

    public componentWillUnmount(): void {
        this._store.removeChangedListener(this._onVirtualMachineGroupStoreGetChange);
        StoreManager.DeleteStore<VirtualMachineGroupStore>(VirtualMachineGroupStore, this.getInstanceId());
        ActionCreatorManager.DeleteActionCreator<VirtualMachineGroupActionCreator>(VirtualMachineGroupActionCreator, this.getInstanceId());
        super.componentWillUnmount();
    }

    private _store: VirtualMachineGroupStore;
    private _actionCreator: VirtualMachineGroupActionCreator;

    private _environmentId: number;
    private _virtualMachineGroupId: number;

    private resourcesTabKey: string = "resources";
    private readonly _selectedTabId = new ObservableValue<string>(this.resourcesTabKey);
}

Components.add("virtualmachinegroups-summary-component", VirtualMachineGroupsContainer);
